import { gql } from '@/__generated__/gql';
import { ITab, MainTabsMobile, TabsDetailsTitleMobile, TabsWrapMobile } from '@/components/basicComponents/tabs';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { initialValuesFromTable } from '@/components/table/constants';
import { ISort } from '@/components/table/types';
import { useResponsive } from '@/hooks/use-responsive';
import { useGoBack } from '@/hooks/useGoBack';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Details } from '../generateSignaturePacket-page/details';
import { ActivityLogTab } from './activityLogTab';
import { DocusignRoutingTab } from './docusignRoutingTab';
import { GET_ENVELOPE_RECIPIENTS_TOTAL_QUERY, LIST_WET_SIGNATURE_UPLOAD_HISTORY_QUERY } from './queries';
import { SignaturePacketTab } from './signaturePacketTab';
import { WetSignatureUploadHistoryResponseDto } from './types';

const GET_ALLOCATION_QUERY = gql(`
  query TransactionDetailsPageAllocation($id: String!) {
    Allocation(id: $id) {
      id
      committedCapital
      allocationStatus
      reason
      suitability
      terminationReason
      terminationNote
      complianceBy {
        firstName
        lastName
        email
      }
      terminateBy {
        id
        firstName
        lastName
      }
      terminatedAt
      investment {
        id
        name
        investorDocsDue
      }
      legalEntity {
        id
        entityName
        taxExempt
        entityType
        otherType
      }
      investmentVehicle {
        id
        name
        taxExempt
      }
      operationsWorkflowTransitionTask {
        id
        name
      }
    }
  }
`);

enum TRANSACTION_DETAILS_TABS_ENUM {
  ACTIVITY_LOG = 'Activity Log',
  DOCUSIGN_ROUTING = 'Docusign Routing',
  SIGNATURE_PACKET = 'Wet Signatures'
}

export const TransactionDetails = () => {
  const goBack = useGoBack();
  const params = useParams();
  const { isMobile } = useResponsive();
  const [searchParams, setSearchParams] = useSearchParams();

  const backToTitle = searchParams.get('backToTitle') || '';
  const activeTab = searchParams.get('tab') || TRANSACTION_DETAILS_TABS_ENUM.ACTIVITY_LOG;
  const key = searchParams.get('key');
  const asc = searchParams.get('asc') === 'true' || false;
  const limit = searchParams.get('limit') || initialValuesFromTable.initialAmountRowsPerPage[0];
  const offset = searchParams.get('offset') || 0;

  const [tabs, setTabs] = useState<ITab[]>([]);
  const [isTabMenuOpen, setIsTabMenuOpen] = useState(false);

  const { data: allocationData, loading: allocationLoading } = useQuery(GET_ALLOCATION_QUERY, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      id: params?.allocationId
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { data: envelopeRecipientsTotalData, loading: envelopeRecipientsTotalLoading } = useQuery<{ envelopeRecipients: { total: number } }>(
    GET_ENVELOPE_RECIPIENTS_TOTAL_QUERY,
    {
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      variables: {
        data: {
          allocationId: params?.allocationId
        }
      },
      onError: (error) => {
        console.error(error);
      }
    }
  );

  const { data: listWetSignatureUploadHistoryData, loading: listWetSignatureUploadHistoryLoading } = useQuery<{
    listWetSignatureUploadHistory: WetSignatureUploadHistoryResponseDto[];
  }>(LIST_WET_SIGNATURE_UPLOAD_HISTORY_QUERY, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      data: {
        allocationId: params?.allocationId
      }
    },
    onError: (error) => {
      console.error(error);
    }
  });

  useEffect(() => {
    const newTabs: ITab[] = [];
    if (envelopeRecipientsTotalData?.envelopeRecipients?.total) {
      newTabs.push({ value: TRANSACTION_DETAILS_TABS_ENUM.DOCUSIGN_ROUTING, amount: null });
    }

    if (listWetSignatureUploadHistoryData?.listWetSignatureUploadHistory?.length) {
      newTabs.push({ value: TRANSACTION_DETAILS_TABS_ENUM.SIGNATURE_PACKET, amount: null });
    }

    setTabs([{ value: TRANSACTION_DETAILS_TABS_ENUM.ACTIVITY_LOG, amount: null }, ...newTabs]);
  }, [envelopeRecipientsTotalData, listWetSignatureUploadHistoryData]);

  const onChangeActiveTab = (value: string) => {
    setSearchParams({ tab: value }, { replace: true });
    if (isMobile) {
      setIsTabMenuOpen(false);
    }
  };

  const onChangeSort = (value: ISort) => {
    setSearchParams(
      (prev) => {
        prev.set('key', value.key);
        prev.set('asc', value.asc.toString());
        return prev;
      },
      { replace: true }
    );
  };

  const onChangePaginationData = (limit: number, offset: number) => {
    setSearchParams(
      (prev) => {
        prev.set('limit', limit.toString());
        prev.set('offset', offset.toString());
        return prev;
      },
      { replace: true }
    );
  };

  if (allocationLoading || envelopeRecipientsTotalLoading || listWetSignatureUploadHistoryLoading) {
    return <LoaderOnWholeScreen />;
  }

  return (
    <>
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={() => goBack({ fallBack: '/transactions' })} backToTitle={backToTitle} />} />
        <PageTitle title={allocationData?.Allocation?.investment?.name ?? ''} />
        <Details
          detailsData={allocationData?.Allocation ?? null}
          tabs={tabs}
          withStatusTag
          activeTab={activeTab}
          isTabMenuOpen={isTabMenuOpen}
          setActiveTab={onChangeActiveTab}
          setIsTabMenuOpen={setIsTabMenuOpen}
        />
      </MainWrap>
      <MainWrap>
        <PaddingWrap>
          {isTabMenuOpen && isMobile && (
            <MainTabsMobile>
              {tabs.map((tab, tabId) => (
                <TabsWrapMobile key={tab.value + tabId} onClick={() => onChangeActiveTab(tab.value)} isActive={activeTab === tab.value}>
                  <TabsDetailsTitleMobile isActive={activeTab === tab.value}>{tab.value}</TabsDetailsTitleMobile>
                </TabsWrapMobile>
              ))}
            </MainTabsMobile>
          )}
          {!isTabMenuOpen && (
            <>
              {activeTab === TRANSACTION_DETAILS_TABS_ENUM.ACTIVITY_LOG && (
                <ActivityLogTab
                  sort={{ key: key || 'createdAt', asc }}
                  paginationValues={{
                    limit: Number(limit),
                    offset: Number(offset)
                  }}
                  onChangeSort={onChangeSort}
                  onChangePaginationData={onChangePaginationData}
                />
              )}
              {activeTab === TRANSACTION_DETAILS_TABS_ENUM.DOCUSIGN_ROUTING && (
                <DocusignRoutingTab
                  sort={{ key: key || 'routingOrder', asc }}
                  paginationValues={{
                    limit: Number(limit),
                    offset: Number(offset)
                  }}
                  onChangeSort={onChangeSort}
                  onChangePaginationData={onChangePaginationData}
                />
              )}
              {activeTab === TRANSACTION_DETAILS_TABS_ENUM.SIGNATURE_PACKET && (
                <SignaturePacketTab sort={{ key: key || 'versionDate', asc }} onChangeSort={onChangeSort} />
              )}
            </>
          )}
        </PaddingWrap>
      </MainWrap>
    </>
  );
};
